import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Plus, FileText, Star, Clock } from "lucide-react";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { TASK_CATEGORIES } from "@/lib/taskCategoryConfig";
import { useNavigate } from "react-router-dom";

export default function TaskTemplates() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchTemplates();
    }
  }, [user]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('task_templates')
        .select('*')
        .or(`is_system.eq.true,created_by.eq.${user?.id}`)
        .order('usage_count', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (template: any) => {
    try {
      // Increment usage count
      await supabase
        .from('task_templates')
        .update({ usage_count: (template.usage_count || 0) + 1 })
        .eq('id', template.id);

      // Navigate to task creation with template data
      navigate('/governance/tasks', { 
        state: { 
          createFromTemplate: true,
          templateData: template.template_data 
        } 
      });

      toast({
        title: "Template Applied",
        description: "Task creation form loaded with template data",
      });
    } catch (error) {
      console.error('Error using template:', error);
      toast({
        title: "Error",
        description: "Failed to apply template",
        variant: "destructive",
      });
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.template_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" || template.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Task Templates</h1>
            <p className="text-muted-foreground mt-2">
              Quickly create tasks from pre-configured templates
            </p>
          </div>
          <Button onClick={() => navigate('/governance/tasks')}>
            <Plus className="h-4 w-4 mr-2" />
            Create Custom Task
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-64">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {TASK_CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="text-center py-12">Loading templates...</div>
        ) : filteredTemplates.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Templates Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery || filterCategory !== "all" 
                ? "Try adjusting your filters" 
                : "No templates available yet"}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map(template => {
              const category = TASK_CATEGORIES.find(c => c.id === template.category);
              const CategoryIcon = category?.icon;

              return (
                <Card key={template.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {CategoryIcon && (
                            <div className="p-2 rounded-lg bg-primary/10">
                              <CategoryIcon className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          {template.is_system && (
                            <Badge variant="secondary" className="gap-1">
                              <Star className="h-3 w-3" />
                              System
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg">{template.template_name}</CardTitle>
                        {template.description && (
                          <CardDescription className="mt-2 line-clamp-2">
                            {template.description}
                          </CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Used {template.usage_count || 0}x
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setPreviewOpen(true);
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleCreateFromTemplate(template)}
                        >
                          Use Template
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedTemplate?.template_name}</DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">
                    {selectedTemplate.description || 'No description provided'}
                  </p>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Category</h4>
                  <Badge>{selectedTemplate.category}</Badge>
                </div>

                {selectedTemplate.template_data?.checkpoints && (
                  <div>
                    <h4 className="font-medium mb-2">Checkpoints ({selectedTemplate.template_data.checkpoints.length})</h4>
                    <ul className="space-y-1">
                      {selectedTemplate.template_data.checkpoints.map((cp: any, idx: number) => (
                        <li key={idx} className="text-sm">• {cp.title}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedTemplate.template_data?.estimated_hours && (
                  <div>
                    <h4 className="font-medium mb-2">Estimated Time</h4>
                    <p className="text-sm">{selectedTemplate.template_data.estimated_hours} hours</p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                    Close
                  </Button>
                  <Button onClick={() => {
                    handleCreateFromTemplate(selectedTemplate);
                    setPreviewOpen(false);
                  }}>
                    Use This Template
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
